/**
 * Readers for the DS column `meta`. TanStack's `ColumnMeta` is an empty interface by default, so
 * the value is typed `{}` — `readColumnMeta` performs the SINGLE, contained assertion that casts
 * it to the DS shape (the DS owns `meta` via `createColumns`). Kept in one place so the unchecked
 * cast doesn't scatter across read sites; a `declare module` augmentation would remove it entirely
 * but is deferred (it fights the dual oxlint/eslint gates on the augmentation `interface`).
 *
 * A plain `.ts` module (not the header-cell component file) so those component files export only
 * components — the fast-refresh purity `only-export-components` asks for.
 */
import type { Cell, RowData } from '@tanstack/react-table'

import type { DataTableColumnMeta } from '../data-table.types'
import type { DataTableAlign } from './data-table.styles'

/** Default CSS floor for a flexible `meta.grow` column. */
export const DEFAULT_GROW_COLUMN_MIN_WIDTH = 96

/**
 * The type args are only ever supplied by the SUMMARY-CELL read below, whose template is
 * the one member of the meta that is generic in the row/value; every other reader takes
 * the defaults.
 */
export const readColumnMeta = <TData extends RowData = RowData, TValue = unknown>(column: {
  columnDef: { meta?: unknown }
}): DataTableColumnMeta<TData, TValue> | undefined =>
  column.columnDef.meta as DataTableColumnMeta<TData, TValue> | undefined

/** Column alignment from the DS column `meta`, applied to the BODY cells. */
export const readColumnAlign = (column: {
  columnDef: { meta?: unknown }
}): DataTableAlign | undefined => readColumnMeta(column)?.align

/**
 * `flex-grow` for a grow column: the caller's `growFactor`, else 1 (an equal share).
 * Read through one helper so the header and body planes cannot disagree about a track.
 */
export const readGrowFactor = (column: { columnDef: { meta?: unknown } }): number =>
  readColumnMeta(column)?.growFactor ?? 1

/** CSS floor for a grow column, retaining the caller's authored `minSize` when present. */
export const readGrowColumnMinWidth = (column: {
  columnDef: { meta?: unknown; minSize?: number }
}): number =>
  readColumnMeta(column)?.growMinWidth ?? column.columnDef.minSize ?? DEFAULT_GROW_COLUMN_MIN_WIDTH

/**
 * Header-cell alignment: the explicit `headerAlign` when set, else the column's `align`.
 * The fallback is what keeps a single `align` governing the whole column, so only a
 * genuinely split column (MUI's centred header over right-aligned numerics) needs both.
 */
export const readColumnHeaderAlign = (column: {
  columnDef: { meta?: unknown }
}): DataTableAlign | undefined => {
  const meta = readColumnMeta(column)

  return meta?.headerAlign ?? meta?.align
}

/**
 * The column's SUMMARY-band cell renderer, or `undefined` when the band should render
 * the column's ordinary `cell`. Typed against the cell's own row/value so the band can
 * `flexRender` it with that cell's context — the reason `readColumnMeta` is generic.
 */
export const readSummaryCell = <TData extends RowData, TValue>(
  cell: Cell<TData, TValue>,
): DataTableColumnMeta<TData, TValue>['summaryCell'] =>
  readColumnMeta<TData, TValue>(cell.column)?.summaryCell
